import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { GetEmpresaActivaUsuarioDtoProps } from "api/DTOs/GetEmpresaActivaUsuarioDto"
import {
  GetAllEmpresasGestionadasVerificadasUsuario,
  GetUsuarioByFiltersParamsProps,
  getUsuarioByCurrentUser,
  getUsuarioByFilters,
  getUsuarioEmpresaActiva,
  getUsuarioEmpresas,
  getValidacionesPendientesUsuario,
} from "api/Usuario.service"
import axios from "axios"
import notification from "common/utils/notification"
import { useRef } from "react"
import { useTranslation } from "react-i18next"
import { convertImgBase64 } from "utils/convertBase64"
import { UpdateEmpresaCommandProps } from "./Commands/UpdateEmpresaCommand"
import { putEmpresaById } from "./Empresa.service"
import { GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable } from "./Schemas/GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable"
import { GetAllEmpresasUsuarioDtoIPagedEnumerableProps } from "./Schemas/GetAllEmpresasUsuarioDtoIPagedEnumerable"
import { TIPO_EMPRESA_USUARIO, TipoEmpresaType } from "./Types/TipoEmpresa"
import { getUsuarioByCurrentUserAdapter } from "./Usuario.adapter"

/**
 * Custom hook that provides functionality related to the active company for a user.
 * It fetches the active company data, allows updating the active company, and provides various related properties/functions.
 *
 * @returns An object containing the following properties and functions:
 *   - activeCompany: The active company data, including the logo as a base64 string.
 *   - activeCompanyIsLoading: A boolean indicating whether the active company data is currently being loaded.
 *   - activeCompanyError: An error object if there was an error while fetching the active company data.
 *   - activeCompanyInvalidation: A function to invalidate the active company data in the query cache.
 *   - activeCompanyMutation: A function to update the active company data.
 */

export interface ActiveCompanyUserHookProps
  extends GetEmpresaActivaUsuarioDtoProps {
  asesoria?: GetEmpresaActivaUsuarioDtoProps["asesoria"] & {
    logoAsBase64?: string
  }
  logoAsBase64?: string
}
export function useUsuarioEmpresaActiva() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const {
    data: empresaActiva,
    isLoading: activeCompanyIsLoading,
    error: activeCompanyError,
    refetch: activeCompanyRefetch,
    isFetching: activeCompanyIsFetching,
    isFetched: activeCompanyIsFetched,
  } = useQuery({
    queryKey: ["getUsuarioEmpresaActiva"],
    queryFn: () => getUsuarioEmpresaActiva(),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 2,
    retry: false,
    retryOnMount: false,
  })

  const activeCompanyInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getUsuarioEmpresaActiva"] })

  const { mutateAsync: activeCompanyMutation } = useMutation({
    mutationFn: async (queryParams: UpdateEmpresaCommandProps) =>
      putEmpresaById(queryParams).then(() => {
        activeCompanyInvalidation()
      }),
  })

  const activeCompanyMutationSucces = () =>
    notification.success({
      body: `${t("notification.companyCorrectUpdate")}`,
    })

  const activeCompany: ActiveCompanyUserHookProps = {
    ...empresaActiva,
    asesoria: {
      ...empresaActiva?.asesoria,
      logoAsBase64: convertImgBase64({
        base64: empresaActiva?.asesoria?.logo?.base64,
        mediaType: empresaActiva?.asesoria?.logo?.mediaType,
      }),
    },
    logoAsBase64: convertImgBase64({
      base64: empresaActiva?.logo?.base64,
      mediaType: empresaActiva?.logo?.mediaType,
    }),
  }

  return {
    activeCompany,
    activeCompanyIsLoading,
    activeCompanyError,
    activeCompanyIsFetching,
    activeCompanyIsFetched,
    activeCompanyRefetch,
    activeCompanyMutationSucces,
    activeCompanyInvalidation,
    activeCompanyMutation,
  }
}

/**
 * Custom hook for fetching and managing usuarioEmpresas data.
 * @returns An object containing the following properties and functions:
 *   - usuarioEmpresas: The user company data.
 *   - usuarioEmpresasIsLoading: A boolean indicating whether the user company data is currently being loaded.
 *   - usuarioEmpresasError: An error object if there was an error while fetching the user company data.
 *   - usuarioEmpresasInvalidation: A function to invalidate the user company data in the query cache.
 */
export function useUsuarioEmpresas() {
  const queryClient = useQueryClient()
  const {
    data: usuarioEmpresasResponse,
    isLoading: usuarioEmpresasIsLoading,
    error: usuarioEmpresasError,
  } = useQuery({
    queryKey: ["getUsuarioEmpresas"],
    queryFn: () => getUsuarioEmpresas(),
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })
  const usuarioEmpresasInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getUsuarioEmpresas"] })

  const usuarioEmpresas: GetAllEmpresasUsuarioDtoIPagedEnumerableProps & {
    logoAsBase64?: string
  } = {
    currentPage: usuarioEmpresasResponse?.currentPage,
    items: usuarioEmpresasResponse?.items?.map((empresa) => ({
      ...empresa,
      logoAsBase64: convertImgBase64({
        base64: empresa?.logo?.base64,
        mediaType: empresa?.logo?.mediaType,
      }),
    })),
    totalCount: usuarioEmpresasResponse?.totalCount,
  }

  return {
    usuarioEmpresas,
    usuarioEmpresasIsLoading,
    usuarioEmpresasError,
    usuarioEmpresasInvalidation,
  }
}

/**
 * Custom hook to fetch the current user's information.
 * @returns An object containing the current user's information, loading state, error state, and a function to invalidate the query.
 */
export function useGetUsuarioByCurrentUser() {
  const queryClient = useQueryClient()
  const {
    data: usuarioResponse,
    isLoading: usuarioIsLoading,
    error: usuarioError,
  } = useQuery({
    queryKey: ["getUsuarioByCurrentUser"],
    queryFn: async () => {
      const response = await getUsuarioByCurrentUser()
      return response
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })
  const usuarioInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getUsuarioByCurrentUser"] })

  const usuario = getUsuarioByCurrentUserAdapter(usuarioResponse)

  return {
    usuario,
    usuarioIsLoading,
    usuarioError,
    usuarioInvalidation,
  }
}

/**
 * Custom hook to fetch the current user's information.
 * @returns An object containing the current user's information, loading state, error state, and a function to invalidate the query.
 */
export function useGetUsuarioByFilters(
  queryParams?: GetUsuarioByFiltersParamsProps
) {
  const queryClient = useQueryClient()
  const {
    data: usuarioByFilters,
    isLoading: usuarioByFiltersIsLoading,
    error: usuarioByFiltersError,
  } = useQuery({
    queryKey: ["getUsuarioByFilters", queryParams],
    queryFn: () => getUsuarioByFilters(queryParams),
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })
  const usuarioByFiltersInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getUsuarioByFilters"] })

  return {
    usuarioByFilters,
    usuarioByFiltersIsLoading,
    usuarioByFiltersError,
    usuarioByFiltersInvalidation,
  }
}

/**
 * Custom hook to fetch all managed and verified companies for a user.
 * @param {Object} options - The options for fetching the companies.
 * @param {TipoEmpresaType} options.tipoEmpresa - The type of company.
 * @returns {Object} - The result of the hook.
 * @property {GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable & { logoAsBase64?: string }} usuarioEmpresasGestionadas - The fetched companies with optional logo as base64.
 * @property {boolean} usuarioEmpresasGestionadasIsLoading - Indicates if the companies are currently being fetched.
 * @property {Error} usuarioEmpresasGestionadasError - The error that occurred during fetching the companies.
 * @property {Function} usuarioEmpresasGestionadasInvalidation - Function to invalidate the query cache for the fetched companies.
 */
export function useGetAllEmpresasGestionadasVerificadasUsuario({
  tipoEmpresa,
}: {
  tipoEmpresa?: TipoEmpresaType
}) {
  const queryClient = useQueryClient()
  const {
    data: usuarioEmpresasGestionadasResponse,
    isLoading: usuarioEmpresasGestionadasIsLoading,
    error: usuarioEmpresasGestionadasError,
  } = useQuery({
    queryKey: ["GetAllEmpresasGestionadasVerificadasUsuario", tipoEmpresa],
    queryFn: async () => {
      if (tipoEmpresa === TIPO_EMPRESA_USUARIO.ASESORIA) {
        const response = await GetAllEmpresasGestionadasVerificadasUsuario()
        return response
      }
      return Promise.resolve(null)
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })
  const usuarioEmpresasGestionadasInvalidation = () =>
    queryClient.invalidateQueries({
      queryKey: ["GetAllEmpresasGestionadasVerificadasUsuario"],
    })

  const usuarioEmpresasGestionadas: GetAllEmpresasGestionadasVerificadasUsuarioDtoIPagedEnumerable & {
    logoAsBase64?: string
  } = {
    currentPage: usuarioEmpresasGestionadasResponse?.currentPage,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items: usuarioEmpresasGestionadasResponse?.items?.map(
      (empresa: $TSMigrationFixMe) => ({
        ...empresa,
        logoAsBase64: convertImgBase64({
          base64: empresa?.logo?.base64,
          mediaType: empresa?.logo?.mediaType,
        }),
      })
    ),
    totalCount: usuarioEmpresasGestionadasResponse?.totalCount,
  }

  return {
    usuarioEmpresasGestionadas,
    usuarioEmpresasGestionadasIsLoading,
    usuarioEmpresasGestionadasError,
    usuarioEmpresasGestionadasInvalidation,
  }
}

export function useValidacionesPendientesUsuario() {
  const queryClient = useQueryClient()
  const ValidacionesPendientesCancelToken = useRef(axios.CancelToken.source())
  const {
    data: usuarioValidacionesPendienteResponse,
    isLoading: usuarioValidacionesPendienteIsLoading,
    error: usuarioValidacionesPendienteError,
    refetch: usuarioValidacionesPendienteRefetch,
  } = useQuery({
    queryKey: [
      "getValidacionesPendientesUsuario",
      ValidacionesPendientesCancelToken.current.token,
    ],
    queryFn: () =>
      getValidacionesPendientesUsuario(
        ValidacionesPendientesCancelToken.current.token
      ),
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })
  const usuarioValidacionesPendienteInvalidation = () =>
    queryClient.invalidateQueries({
      queryKey: ["getValidacionesPendientesUsuario"],
    })

  return {
    usuarioValidacionesPendienteResponse,
    usuarioValidacionesPendienteIsLoading,
    usuarioValidacionesPendienteError,
    usuarioValidacionesPendienteRefetch,
    usuarioValidacionesPendienteInvalidation,
  }
}
